import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LanguageService } from 'src/app/services/language.service';
import { PageFeedbackService } from '../../services/page-feedback.service';

export type PageFeedbackStatus = 'idle' | 'submitting' | 'submitted' | 'error';

/** sessionStorage key prefix: one submission per page per session. */
export const PAGE_FEEDBACK_STORAGE_PREFIX = 'uc-page-feedback:';
export const PAGE_FEEDBACK_COMMENT_MAX = 1000;

/**
 * "Was this page helpful?" (Unified Code criterion 40): Yes / No, an optional comment, then a confirmation
 * that replaces the form. The page is identified by its path; revisiting a rated page shows the confirmation.
 */
@Component({
  selector: 'app-page-feedback',
  templateUrl: './page-feedback.component.html',
})
export class PageFeedbackComponent implements OnInit, OnDestroy {
  readonly commentMax = PAGE_FEEDBACK_COMMENT_MAX;
  pageUrl = '';
  isHelpful: boolean | null = null;
  comment = '';
  status: PageFeedbackStatus = 'idle';

  @ViewChild('confirmation') confirmation?: ElementRef<HTMLElement>;

  private readonly subscriptions = new Subscription();

  constructor(
    private feedbackService: PageFeedbackService,
    private languageService: LanguageService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.load();
    // The widget lives in the app shell, so it resets for every page the visitor opens.
    this.subscriptions.add(this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => this.load()));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Current page path (overridable in specs). */
  readPath(): string {
    return window.location.pathname || '/';
  }

  load(): void {
    const path = this.readPath();
    // Query-string changes on the same page (search, filters) keep the visitor's choice.
    if (path === this.pageUrl) return;
    this.pageUrl = path;
    this.isHelpful = null;
    this.comment = '';
    this.status = this.wasSubmitted(path) ? 'submitted' : 'idle';
  }

  choose(isHelpful: boolean): void {
    if (this.status === 'submitting') return;
    this.isHelpful = isHelpful;
    if (this.status === 'error') this.status = 'idle';
  }

  /** Radio group keyboard support: any arrow key toggles between Yes and No. */
  onChoiceKeydown(event: KeyboardEvent, buttons: HTMLElement[]): void {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const next = !(this.isHelpful ?? false);
    this.choose(next);
    buttons[next ? 0 : 1]?.focus();
  }

  get commentLength(): number {
    return (this.comment ?? '').length;
  }

  get canSubmit(): boolean {
    return this.isHelpful !== null && this.status !== 'submitting' && this.commentLength <= this.commentMax;
  }

  submit(): void {
    if (!this.canSubmit) return;
    this.status = 'submitting';
    const pageUrl = this.pageUrl;
    const comment = (this.comment ?? '').trim();

    this.feedbackService
      .submit({
        pageUrl,
        isHelpful: this.isHelpful as boolean,
        comment: comment || null,
        culture: this.languageService.getCurrentLanguage(),
      })
      .subscribe({
        next: () => {
          this.remember(pageUrl);
          this.status = 'submitted';
          // Keep keyboard and screen reader users on the confirmation that replaces the form.
          setTimeout(() => this.confirmation?.nativeElement.focus());
        },
        error: () => (this.status = 'error'),
      });
  }

  private wasSubmitted(path: string): boolean {
    try {
      return sessionStorage.getItem(PAGE_FEEDBACK_STORAGE_PREFIX + path) !== null;
    } catch {
      return false;
    }
  }

  private remember(path: string): void {
    try {
      sessionStorage.setItem(PAGE_FEEDBACK_STORAGE_PREFIX + path, new Date().toISOString());
    } catch {
      // Storage unavailable (private mode): the confirmation is still shown for this visit.
    }
  }
}
